import { useState, type FormEvent } from 'react';
import { request } from '@/lib/api.js';
import { useToast } from '@/components/Toast.js';
import { GlassButton } from '@/components/ui/index.js';
import type { GenerateCodesResponse } from '@/lib/types.js';

/**
 * Batch redeem-code generation form. Encapsulates its own local state
 * (count / plan / batch / generated list) so CodesPage doesn't have to
 * carry it. Parent only cares about "did anything happen → refresh".
 */

interface Props {
  /** Called after a successful generation so the parent can re-fetch. */
  onGenerated: () => Promise<void> | void;
}

export function GenerateForm({ onGenerated }: Props): JSX.Element {
  const [count, setCount] = useState(10);
  const [plan, setPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [batch, setBatch] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<string[] | null>(null);

  async function handleGenerate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setGenerating(true);
    setGenerated(null);
    try {
      const body: { count: number; plan: 'monthly' | 'yearly'; batch_id?: string } = {
        count,
        plan,
      };
      if (batch !== '') body.batch_id = batch;
      const res = await request<GenerateCodesResponse>('/admin/redeem_codes/generate', {
        method: 'POST',
        body,
      });
      setGenerated(res.codes);
      await onGenerated();
    } catch (err) {
      useToast.getState().show('error', err instanceof Error ? err.message : '生成失败');
    } finally {
      setGenerating(false);
    }
  }

  function copyAll(): void {
    if (generated === null) return;
    void navigator.clipboard.writeText(generated.join('\n'));
  }

  return (
    <section className="mat-matte rounded-xl p-6 mb-8">
      <h2 className="text-sm font-semibold text-slate-300 mb-2">批量生成</h2>
      <p className="text-xs text-slate-500 mb-4">
        🎟️ 兑换码用户激活后 <strong className="text-shark-300">直接开通会员</strong>
        （月卡 = 1 个月，年卡 = 1 年，效果同 Stripe 付费）。折扣码是另一套机制
        （结账时打折，不直接送会员），见<span className="text-slate-400">「折扣码」</span>页。
      </p>
      <form onSubmit={(event): void => { void handleGenerate(event); }} className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="gen-count" className="block text-xs text-slate-400 mb-1">数量</label>
          <input
            type="number"
            min={1}
            max={500}
            id="gen-count"
            value={count}
            onChange={(e): void => setCount(Math.max(1, Number(e.target.value)))}
            className="w-24 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200"
          />
        </div>
        <div>
          <label htmlFor="gen-plan" className="block text-xs text-slate-400 mb-1">套餐</label>
          <select
            id="gen-plan"
            value={plan}
            onChange={(e): void => setPlan(e.target.value as 'monthly' | 'yearly')}
            className="px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200"
          >
            <option value="monthly">月卡（1 个月会员）</option>
            <option value="yearly">年卡（1 年会员）</option>
          </select>
        </div>
        <div>
          <label htmlFor="gen-batch" className="block text-xs text-slate-400 mb-1">批次（选填）</label>
          <input
            type="text"
            id="gen-batch"
            value={batch}
            onChange={(e): void => setBatch(e.target.value)}
            placeholder="闲鱼-2026-06"
            className="w-48 px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-slate-200 placeholder:text-slate-500"
          />
        </div>
        <GlassButton type="submit" variant="primary" disabled={generating}>
          {generating ? '生成中…' : '生成'}
        </GlassButton>
      </form>

      {generated !== null && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-felt-400">
              已生成 {generated.length} 个 {plan === 'monthly' ? '月卡' : '年卡'}
            </span>
            <GlassButton size="sm" variant="ghost" onClick={copyAll}>
              复制全部
            </GlassButton>
          </div>
          <pre className="text-xs font-mono bg-slate-950/60 border border-white/10 rounded p-3 max-h-48 overflow-y-auto">
            {generated.join('\n')}
          </pre>
        </div>
      )}
    </section>
  );
}
